import { IconButton, Tooltip } from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { useTheme } from '../../hooks/useTheme';
import { usePreferenceWriter } from '../../hooks/useCurrentUser';

/**
 * The header light/dark switch.
 *
 * Persists the choice (#528). It used to dispatch into the theme slice and
 * stop there, so the preference lived only in this tab: the server still held
 * the old theme, and the next thing to save preferences — clicking All / Mine /
 * Unclaimed — wrote that old theme back and the palette snapped round.
 *
 * The operator's reading was reasonable: "changing view resets my theme". What
 * actually happened is that the theme was never saved and something else
 * restored it.
 */
export const ThemeToggle: React.FC = () => {
  const { isDarkMode, toggle } = useTheme();
  // usePreferenceWriter, not useCurrentUser: this control saves a setting, it
  // does not own loading the account. Calling the loader here made rendering a
  // theme button trigger GET /me (#528).
  const { loaded, savePreference } = usePreferenceWriter();

  const onClick = () => {
    // Flip locally first: the palette should change on the click, not on the
    // round trip.
    toggle();
    // Only once the stored document has arrived. Saving before then would
    // write the *defaults* for everything else in it — silently resetting the
    // planner and fleet settings to make a theme choice stick.
    if (loaded) {
      // Derived from isDarkMode, mirroring the reducer's own rule
      // (`mode === 'light' ? 'dark' : 'light'`). Computing this from
      // `preference` instead would save the wrong value whenever the current
      // preference is `system`, because then the preference and the rendered
      // mode are not the same thing.
      void savePreference({ theme: isDarkMode ? 'light' : 'dark' });
    }
  };

  return (
    <Tooltip title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}>
      <IconButton onClick={onClick} color="inherit">
        {isDarkMode ? <Brightness7 /> : <Brightness4 />}
      </IconButton>
    </Tooltip>
  );
};
